/**
 * Pre-validation utilities for product-version feature
 * Leverages cloud-core validation logic to validate data before API calls
 */
import { FlowjoCommons } from '@portal/portal-core';

const { Regional } = FlowjoCommons;

interface ValidationResult {
  valid: boolean;
  error?: string;
  warning?: string;
  invalidCodes?: string[];
  embargoedCodes?: string[];
}

interface EmbargoFilterResult {
  embargoedCodes: string[];
  nonEmbargoedCodes: string[];
}

/**
 * Validates a single country code
 * @param countryCode - Two-letter country code
 * @returns Validation result with valid flag and optional error
 */
export const validateCountryCode = (
  countryCode: string | null | undefined,
): ValidationResult => {
  if (!countryCode || typeof countryCode !== 'string') {
    return { valid: false, error: 'Country code is required and must be a string' };
  }

  const upperCode = countryCode.toUpperCase();

  if (upperCode.length !== 2) {
    return { valid: false, error: `"${countryCode}" must be exactly 2 characters` };
  }

  if (!(Regional.COUNTRY_NAMES_BY_CODE as Record<string, string>)[upperCode]) {
    return { valid: false, error: `"${countryCode}" is not a valid country code` };
  }

  return { valid: true };
};

/**
 * Validates an array of country codes
 * @param countryCodes - Array of two-letter country codes
 * @returns Validation result with valid flag, optional error, and invalidCodes array
 */
export const validateCountryCodes = (countryCodes: unknown): ValidationResult => {
  if (!Array.isArray(countryCodes)) {
    return { valid: false, error: 'Country codes must be an array' };
  }

  const invalidCodes: string[] = countryCodes.filter((code) => {
    const result = validateCountryCode(code);
    return !result.valid;
  });

  if (invalidCodes.length > 0) {
    return {
      valid: false,
      error: `Invalid country codes: ${invalidCodes.join(', ')}`,
      invalidCodes,
    };
  }

  return { valid: true };
};

/**
 * Checks if a country code is embargoed
 * @param countryCode - Two-letter country code
 * @returns True if the country is embargoed
 */
export const isEmbargoedCountry = (countryCode: string | null | undefined): boolean => {
  if (!countryCode) return false;
  const upperCode = countryCode.toUpperCase();
  return !!(Regional.embargoedCountries as Record<string, string>)[upperCode];
};

/**
 * Filters out embargoed countries from an array of country codes
 * @param countryCodes - Array of two-letter country codes
 * @returns Object containing embargoedCodes and nonEmbargoedCodes arrays
 */
export const filterEmbargoedCountries = (countryCodes: unknown): EmbargoFilterResult => {
  if (!Array.isArray(countryCodes)) {
    return { embargoedCodes: [], nonEmbargoedCodes: [] };
  }

  const embargoedCodes: string[] = countryCodes
    .filter((code) => {
      const upperCode = code.toUpperCase();
      return !!(Regional.embargoedCountries as Record<string, string>)[upperCode];
    })
    .map((code) => code.toUpperCase());

  const nonEmbargoedCodes: string[] = countryCodes
    .filter((code) => {
      const upperCode = code.toUpperCase();
      return !(Regional.embargoedCountries as Record<string, string>)[upperCode];
    })
    .map((code) => code.toUpperCase());

  return { embargoedCodes, nonEmbargoedCodes };
};

/**
 * Validates country codes and checks for embargoed countries.
 * Warns about embargoed countries but doesn't fail validation.
 * @param countryCodes - Array of country codes
 * @returns Validation result with valid flag, optional error/warning, invalidCodes and embargoedCodes arrays
 */
export const validateCountryCodesWithEmbargoWarning = (
  countryCodes: unknown,
): ValidationResult => {
  // First validate all codes are valid
  const validation = validateCountryCodes(countryCodes);
  if (!validation.valid) {
    return validation;
  }

  // Then check for embargoed countries (warning, not error)
  const { embargoedCodes } = filterEmbargoedCountries(countryCodes);

  if (embargoedCodes.length > 0) {
    const embargoedNames = embargoedCodes.map(
      (code) => `${Regional.getCountryName(code)} (${code})`,
    );
    return {
      valid: true,
      warning: `Warning: Embargoed countries detected: ${embargoedNames.join(
        ', ',
      )}. These countries cannot be whitelisted per company policy.`,
      embargoedCodes,
    };
  }

  return { valid: true };
};

/**
 * Validates a product name constant
 * @param productName - Product name constant
 * @param validProductNames - Array of valid product name constants
 * @returns Validation result with valid flag and optional error
 */
export const validateProductName = (
  productName: string | null | undefined,
  validProductNames: unknown,
): ValidationResult => {
  if (!productName || typeof productName !== 'string') {
    return { valid: false, error: 'Product name is required' };
  }

  if (!Array.isArray(validProductNames) || validProductNames.length === 0) {
    return { valid: false, error: 'Valid product names list is not provided' };
  }

  if (!validProductNames.includes(productName)) {
    return {
      valid: false,
      error: `"${productName}" is not a valid product. Valid products: ${validProductNames.join(
        ', ',
      )}`,
    };
  }

  return { valid: true };
};

/**
 * Validates a semantic version string (X.Y.Z format)
 * @param version - Semantic version string
 * @returns Validation result with valid flag and optional error
 */
export const validateSemanticVersion = (
  version: string | null | undefined,
): ValidationResult => {
  if (!version || typeof version !== 'string') {
    return { valid: false, error: 'Version is required' };
  }

  const semverRegex = /^\d+\.\d+\.\d+$/;
  if (!semverRegex.test(version)) {
    return {
      valid: false,
      error: `"${version}" is not a valid semantic version. Must be in format X.Y.Z (e.g., 1.0.0)`,
    };
  }

  return { valid: true };
};

/**
 * Validates a URL string
 * @param url - URL string to validate
 * @returns Validation result with valid flag and optional error
 */
export const validateUrl = (url: string | null | undefined): ValidationResult => {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: 'URL is required' };
  }

  try {
    const urlObj = new URL(url.trim());
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return { valid: false, error: 'URL must use HTTP or HTTPS protocol' };
    }
    return { valid: true };
  } catch (e) {
    return { valid: false, error: `Invalid URL: ${(e as Error).message}` };
  }
};

/**
 * Normalizes country codes to uppercase
 * @param countryCodes - Array of country codes
 * @returns Normalized country codes in uppercase
 */
export const normalizeCountryCodes = (countryCodes: unknown): string[] => {
  if (!Array.isArray(countryCodes)) {
    return [];
  }
  return countryCodes.map((code) =>
    typeof code === 'string' ? code.toUpperCase() : code,
  );
};
